import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Globe, RefreshCw, Loader2, TrendingUp, Users, Package, ShoppingCart } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGlobalDashboard = () => {
  const [carregando, setCarregando] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [integracao, setIntegracao] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [respDash, respInt] = await Promise.all([
        authService.fetchAuth(`${BACKEND_URL}/api/admin-global/dashboard`),
        authService.fetchAuth(`${BACKEND_URL}/api/cross-module/overview`),
      ]);
      if (respDash.ok) setDashboard(await respDash.json());
      if (respInt.ok) setIntegracao(await respInt.json());
    } catch (error) {
      console.error("Erro ao carregar dashboard global:", error);
      toast.error("Não foi possível carregar o dashboard global.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando dashboard global...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Globe className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Dashboard Global</h1>
            <p className="text-sm text-muted-foreground">Brasil + Internacional, numa visão só</p>
          </div>
        </div>
        <Button variant="outline" onClick={carregar}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* BRASIL */}
        <Card className="p-5 border-green-200 bg-green-50">
          <h2 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
            🇧🇷 Brasil
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-green-700">Afiliados</p>
              <p className="text-xl font-bold text-green-900">{dashboard?.brasil?.afiliados ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-green-700">Fornecedores</p>
              <p className="text-xl font-bold text-green-900">{dashboard?.brasil?.fornecedores ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-green-700">Pedidos</p>
              <p className="text-xl font-bold text-green-900">{dashboard?.brasil?.pedidos ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-green-700">Receita</p>
              <p className="text-xl font-bold text-green-900">
                R$ {(dashboard?.brasil?.receita_brl ?? 0).toFixed(2)}
              </p>
            </div>
          </div>
        </Card>

        {/* INTERNACIONAL */}
        <Card className="p-5 border-blue-200 bg-blue-50">
          <h2 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
            🌎 Internacional
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-blue-700">Afiliados vendendo fora</p>
              <p className="text-xl font-bold text-blue-900">{dashboard?.internacional?.afiliados_vendendo_fora ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-blue-700">Pedidos</p>
              <p className="text-xl font-bold text-blue-900">{dashboard?.internacional?.pedidos ?? 0}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-blue-700">Receita (USD)</p>
              <p className="text-xl font-bold text-blue-900">
                ${(dashboard?.internacional?.receita_usd ?? 0).toFixed(2)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Integração entre módulos */}
      {integracao && (
        <Card className="p-5">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Panorama por módulo
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Produtos BR</p>
                <p className="font-bold">{integracao.brasil?.produtos ?? 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Produtos Internacional</p>
                <p className="font-bold">{integracao.internacional?.produtos ?? 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Links Sociais</p>
                <p className="font-bold">{integracao.social_commerce?.links ?? 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Campanhas Ativas</p>
                <p className="font-bold">{integracao.social_commerce?.campanhas ?? 0}</p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminGlobalDashboard;
